import { useMemo } from "react";
import {
  useV2ListProjectsForAccountQuery,
  type V2ListProjectsForAccountApiArg,
  type V2ProjectResource,
} from "@generated";
import { sortParam } from "../columns/columnRegistry";
import {
  PROJECT_TABS,
  type ColumnSort,
  type IncludedRecord,
  type ProjectRow,
  type ProjectsTab,
  type ProjectsViewFilters,
} from "../types";

export interface ProjectsListParams {
  tab: ProjectsTab;
  search: string;
  page: number;
  pageSize: number;
  filters: ProjectsViewFilters;
  sort: ColumnSort;
}

export const PROJECT_INCLUDES = [
  "client",
  "sales-executive",
  "presales-engineer",
  "change-order-projects",
  "change-orders",
] as const;

type ProjectFilter = NonNullable<V2ListProjectsForAccountApiArg["filter"]>;

const joined = (v?: string[]) => (v && v.length ? v.join(",") : undefined);

export function buildProjectsListArgs(p: ProjectsListParams): V2ListProjectsForAccountApiArg {
  const f = p.filters;
  const statuses = PROJECT_TABS.find((t) => t.key === p.tab)?.statuses ?? [];
  const raw: Record<string, unknown> = {
    active: p.tab !== "archived",
    status: joined(statuses),
    "project-name": p.search.trim() || undefined,
    "my-projects": f.showMyProjects ? true : undefined,
    "client.id": joined(f.clients),
    "sales-executive.id": joined(f.salesExecs),
    "presales-engineer.id": joined(f.presalesEngineers),
    "team.id": joined(f.teams),
    "collaborator.id": joined(f.teammates),
    "tag-list": joined(f.tags),
    "rate-table": joined(f.rateTableIds),
    lob: joined(f.lobIds),
    "business-unit": joined(f.businessUnits),
    "updated-at.after": f.dateEditedStart || undefined,
    "updated-at.before": f.dateEditedEnd || undefined,
    "created-at.after": f.dateCreatedStart || undefined,
    "created-at.before": f.dateCreatedEnd || undefined,
  };
  const filter = Object.fromEntries(
    Object.entries(raw).filter(([, v]) => v !== undefined)
  ) as ProjectFilter; // status is a comma-joined list; the generated enum types one value

  return {
    filter,
    sort: [sortParam(p.sort)] as V2ListProjectsForAccountApiArg["sort"],
    page: { number: p.page, size: p.pageSize },
    include: [...PROJECT_INCLUDES],
  };
}

type RelData = { id: string; type: string } | { id: string; type: string }[] | null | undefined;

const relOne = (r: { relationships?: Record<string, { data?: RelData }> }, name: string) => {
  const d = r.relationships?.[name]?.data;
  return d && !Array.isArray(d) ? d : undefined;
};
const relMany = (r: { relationships?: Record<string, { data?: RelData }> }, name: string) => {
  const d = r.relationships?.[name]?.data;
  return Array.isArray(d) ? d : [];
};

export function normalizeProjects(
  data: V2ProjectResource[],
  included: unknown[] | undefined
): ProjectRow[] {
  const index = new Map<string, IncludedRecord>();
  for (const rec of (included ?? []) as IncludedRecord[]) {
    if (rec?.type && rec?.id != null) index.set(`${rec.type}:${rec.id}`, rec);
  }
  const coNumberByChild = new Map<string, number>();
  index.forEach((rec) => {
    if (rec.type !== "change-orders") return;
    const child = relOne(rec, "child-project");
    const n = rec.attributes?.number;
    if (child && typeof n === "number") coNumberByChild.set(String(child.id), n);
  });

  const lookup = (ref?: { id: string; type: string }) =>
    ref ? index.get(`${ref.type}:${ref.id}`) : undefined;

  const toRow = (p: V2ProjectResource, isChild: boolean): ProjectRow => {
    const rec = p as unknown as IncludedRecord;
    const id = String(p.id);
    const children = isChild
      ? []
      : relMany(rec, "change-order-projects")
          .map((ref) => lookup(ref))
          .filter((c): c is IncludedRecord => !!c)
          .map((c) => toRow(c as unknown as V2ProjectResource, true))
          .sort((a, b) => {
            const an = a.changeOrderNumber;
            const bn = b.changeOrderNumber;
            if (an != null && bn != null) return an - bn;
            if (an != null) return -1;
            if (bn != null) return 1;
            const ad = String(a.project.attributes?.["created-at"] ?? "");
            const bd = String(b.project.attributes?.["created-at"] ?? "");
            return ad.localeCompare(bd);
          });
    return {
      id,
      project: p,
      client: lookup(relOne(rec, "client")),
      salesExecutive: lookup(relOne(rec, "sales-executive")),
      presalesEngineer: lookup(relOne(rec, "presales-engineer")),
      isChangeOrder: isChild || !!p.attributes?.["change-order?"],
      changeOrderNumber: isChild ? coNumberByChild.get(id) : undefined,
      changeOrders: children,
    };
  };

  return data.map((p) => toRow(p, false));
}

export function useProjectsListQuery(p: ProjectsListParams, opts: { skip: boolean }) {
  const args = useMemo(() => buildProjectsListArgs(p), [p]);
  // `data` keeps the previous args' result while a new request is in flight; `currentData` does not.
  // Refetch on revisit: a batch action's refetch() only refreshes the current
  // args, so another tab's cached result would otherwise show the old rows.
  const { currentData, isLoading, isFetching, isError, refetch } = useV2ListProjectsForAccountQuery(args, {
    skip: opts.skip,
    refetchOnMountOrArgChange: true,
  });
  const rows = useMemo(
    () =>
      normalizeProjects(
        (currentData?.data ?? []) as V2ProjectResource[],
        currentData?.included as unknown[] | undefined
      ),
    [currentData]
  );
  const totalRows = (currentData?.meta?.["record-count"] as number | undefined) ?? rows.length;
  const totalPages =
    (currentData?.meta?.["page-count"] as number | undefined) ?? Math.max(1, Math.ceil(totalRows / p.pageSize));
  return {
    rows,
    totalRows,
    totalPages,
    isLoading: isLoading || (isFetching && !currentData),
    isFetching,
    isError,
    refetch,
  };
}

export default useProjectsListQuery;
